import { IconButton } from '@hivebrite/design-system/Icon';

import { Children } from 'hb-builder/components/common/Item';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { defaultTabs } from 'hb-builder/constants/tabs';
import { useComponentContext, useComponentsContext } from 'hb-builder/hooks';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { ButtonItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/buttonItemAttributes';

const componentType = 'single';

const Buttons = () => <div />; // never used

const RenderBuilder = () => {
  const { item } = useComponentContext();
  const { components } = useComponentsContext();

  const { Wrapper } = components;

  if (!item) {
    return null;
  }

  const { attributes } = item;

  if (!attributes || attributes?.hidden) {
    return null;
  }

  return (
    <Wrapper.RenderBuilder attributes={attributes}>
      <Children item={item} />
    </Wrapper.RenderBuilder>
  );
};

const Render = ({
  children,
  item,
}: RenderProps<DocumentItem<ButtonItemAttributes>>) => {
  const { components } = useComponentsContext();
  const { Wrapper } = components;

  if (!item || !item?.children) {
    return null;
  }

  const { attributes } = item;

  if (!attributes || attributes?.hidden) {
    return null;
  }

  return <Wrapper.Render item={item}>{children}</Wrapper.Render>;
};

Buttons.componentName = 'Buttons';
Buttons.componentType = componentType;

Buttons.componentLabel = __('Button(s)');
Buttons.componentCategory = 'single';
Buttons.componentIcon = () => IconButton;

Buttons.canBeDoubleClicked = true;
Buttons.canBeDragged = true;
// always true, else the component would not be rendered at all
Buttons.canBeListed = ({ hasDoubleButtons }) => hasDoubleButtons;
Buttons.canBeSelected = true;

Buttons.RenderBuilder = RenderBuilder;
Buttons.Render = Render;

Buttons.item = (props) => {
  const { components, features } = props;
  const { Button, Wrapper } = components;

  const button = Button.item({
    ...props,
    features: {
      button: {
        containerPadding: '0px',
        content: __('Click here'),
        mode: 'network',
        variant: 'primary',
        tied: true,
      },
    },
  });

  return Wrapper.item({
    ...props,
    attributes: {
      childrenSpacing: 'medium',
      containerBackgroundColor: '',
      containerPadding:
        features?.buttons?.containerPadding || '10px 10px 10px 10px',
      horizontalAlign: 'center',
      letterSpacing: 0,
      tied: features?.buttons?.tied,
    },
    children: [button],
    category: Buttons.componentCategory,
    name: Buttons.componentName,
    type: Buttons.componentName,
  });
};

Buttons.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

Buttons.SettingsTabs = defaultTabs;

export default Buttons;
